import { LpgSection } from '@/components/lpg/LpgSection';
import Layout from '@/components/shared/Layout';
import { SEO } from '@/components/shared/SEO';
import { CN_LANGUAGE, CN_REGION, THEME } from '@/const/cookies';
import { type Page, useLpg } from '@/hooks/useLpg';
import { sharedConfig } from '@/pageConfig/shared';
import { usePartnerId } from '@/store/usePartnerId';
import { useTheme } from '@/store/useTheme';
import { Box } from '@lemonade-hq/cdk';
import { fetchWithRetry } from '@/utils/fetchWithRetry';
import type { GetStaticPaths, GetStaticProps } from 'next';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { useCookies } from 'react-cookie';

const REVALIDATE_DATA = 600; // 10 minutes

export const getStaticPaths: GetStaticPaths = (async () => {
  // this will avoid generating pages in build time on development | staging
  if (process.env.STAGE === 'development' || process.env.STAGE === 'staging' || process.env.STAGE == null) {
    return {
      paths: [],
      fallback: 'blocking',
    };
  }

  const result = await fetchWithRetry<{ data: Page[] }>(`${process.env.NEXT_PUBLIC_LPG_URL}`);
  if (result?.data == null) {
    return { paths: [], fallback: 'blocking' };
  }

  const paths = result.data.map(page => ({
    params: { slug: page.url },
  }));
  return {
    paths,
    fallback: 'blocking',
  };
}) satisfies GetStaticPaths;

export const getStaticProps: GetStaticProps = (async () => {
  const result = await fetchWithRetry<{ data: Page[] }>(`${process.env.NEXT_PUBLIC_LPG_URL}`);
  if (result?.data == null) {
    return { notFound: true };
  }

  return { props: { pages: result.data }, revalidate: REVALIDATE_DATA };
}) satisfies GetStaticProps<{
  pages: Page[];
}>;

export default function LPGPage({ pages }: { pages: Page[] }) {
  const { page, locale, seo, product, isCarPpm } = useLpg(pages);
  const theme = useTheme(state => state.theme);
  const setPartnerId = usePartnerId(state => state.setPartnerId);
  const router = useRouter();

  const [, setCookie] = useCookies();

  useEffect(() => {
    // redirect to home if page is not found
    if (page == null) {
      router.push('/');
      return;
    }
    setPartnerId({ partnerId: page?.partnerId });

    const [lang, region] = locale.split('-');
    const options = { domain: process.env.NEXT_PUBLIC_DOMAIN, path: '/' };

    setCookie(THEME, theme, options);
    setCookie(CN_LANGUAGE, lang, options);
    setCookie(CN_REGION, region.toUpperCase(), options);
  }, [page, setPartnerId, router, theme, setCookie, locale]);

  // important to avoid start with theme pinkish and then switch to reddish
  if (theme == null) {
    return <SEO {...seo} advancedRobots={true} noIndex={true} />;
  }

  return (
    <Box position="relative" overflowX="hidden">
      <Layout headerActions={sharedConfig[locale].layout.headerActions} productType={product} product={product}>
        <SEO {...seo} advancedRobots={true} noIndex={true} />
        <LpgSection page={page} locale={locale} product={product} isCarPpm={isCarPpm} />
      </Layout>
    </Box>
  );
}
